import { useState } from "react";
import { Loader2, Paperclip, Send, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RupiahInput from "@/components/RupiahInput";
import DateField from "@/components/DateField";
import { fmtDate, formatIDR } from "@/lib/constants";

function rowProjectLabel(pc) {
  if (pc.project_type === "variation_order") return `${pc.project_name} · VO ${pc.project_variation || ""}`.trim();
  if (pc.project_type === "working_order") return `${pc.project_name} · WO ${pc.project_wo_number || ""}`.trim();
  return pc.project_name || "Untitled";
}

/** URL bukti transaksi. 2026-10-21: auth via cookie HttpOnly ens_token —
    <a target="_blank"> mengirim cookie otomatis (same-origin), tidak perlu
    query token dari localStorage (sudah dihapus saat migrasi cookie). */
export const pcProofUrl = (pcId, itemId) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/petty-cash/${pcId}/items/${itemId}/proof`;
};

/** Pengaju fills actual expense per item after transfer received. */
export default function PettyCashActualDialog({
  open, pc, items, docNote, onItemFieldChange, onDocNoteChange, onCancel, onSubmit, onProofChanged,
}) {
  // 2026-10-16 (user request): upload bukti transaksi (foto/PDF) per item —
  // Verifikasi 1 mengecek kesesuaian nominal terhadap bukti ini.
  const [proofBusy, setProofBusy] = useState(null);
  const [proofs, setProofs] = useState({});

  const uploadProof = async (rowKey, itemId, file) => {
    if (!file || !pc) return;
    setProofBusy(rowKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/petty-cash/${pc.id}/items/${itemId}/proof`, fd);
      setProofs((p) => ({ ...p, [rowKey]: data }));
      toast.success("Bukti transaksi terupload");
      onProofChanged?.();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal upload bukti transaksi");
    } finally {
      setProofBusy(null);
    }
  };

  const deleteProof = async (rowKey, itemId) => {
    setProofBusy(rowKey);
    try {
      await api.delete(`/petty-cash/${pc.id}/items/${itemId}/proof`);
      setProofs((p) => ({ ...p, [rowKey]: null }));
      toast.success("Bukti transaksi dihapus");
      onProofChanged?.();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menghapus bukti");
    } finally {
      setProofBusy(null);
    }
  };
  if (!pc) return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="rounded-none max-w-4xl" data-testid="dlg-pc-actual"><div /></DialogContent>
    </Dialog>
  );

  const totalPengajuan = pc.total || 0;
  const totalTransfer = pc.transfer_amount != null ? pc.transfer_amount : totalPengajuan;
  const totalAktual = Object.values(items).reduce((s, v) => s + (Number(String(v.actual_amount ?? "").replace(/[^\d.-]/g, "")) || 0), 0);
  const sisaKas = totalTransfer - totalAktual;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="rounded-none w-[92vw] max-w-[92vw] max-h-[90vh] overflow-y-auto" data-testid="dlg-pc-actual">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <Send size={16} className="text-amber-600" /> Isi Realisasi Petty Cash (Per Item)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><div className="overline text-slate-500 text-[10px]">Minggu</div><div className="font-semibold text-slate-800">{pc.week_label}</div></div>
            <div><div className="overline text-slate-500 text-[10px]">Project</div><div className="font-semibold text-slate-800 truncate" title={rowProjectLabel(pc)}>{rowProjectLabel(pc)}</div></div>
            <div><div className="overline text-slate-500 text-[10px]">Ditransfer</div><div className="font-semibold text-emerald-700 tabular-nums">{formatIDR(totalTransfer)}</div>{pc.transfer_date && <div className="text-[9px] text-slate-500">{fmtDate(pc.transfer_date)}</div>}</div>
            <div>
              <div className="overline text-slate-500 text-[10px]">Total Aktual (Live)</div>
              <div className="font-semibold text-amber-700 tabular-nums" data-testid="pc-actual-total-live">{formatIDR(totalAktual)}</div>
              <div className={`text-[9px] font-semibold ${sisaKas >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="pc-actual-sisa-live">Sisa Kas: {formatIDR(sisaKas)}</div>
            </div>
          </div>

          <div className="border border-slate-200 overflow-x-auto" data-testid="pc-actual-table-wrap">
            <table className="w-full text-xs" style={{ tableLayout: "fixed" }} data-testid="pc-actual-items-table">
              {/* 16-PETTY-CASH-DETAIL-COLUMN-LAYOUT-R1: Item paling lebar (300-320px min),
                  wrap 2-3 baris + tooltip. Fixed layout + colgroup supaya proporsi konsisten. */}
              <colgroup>
                <col style={{ minWidth: "300px" }} />
                <col style={{ width: "60px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "170px" }} />
                <col style={{ width: "170px" }} />
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-2 text-left">Item</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-right">Unit Price</th>
                  <th className="px-2 py-2 text-right bg-slate-100">Subtotal</th>
                  <th className="px-2 py-2 text-left bg-amber-50">Tgl Aktual *</th>
                  <th className="px-2 py-2 text-left bg-amber-50">Tgl Voucher *</th>
                  <th className="px-2 py-2 text-right bg-amber-50">Nominal Aktual *</th>
                  <th className="px-2 py-2 text-left bg-amber-50">No. Voucher</th>
                  <th className="px-2 py-2 text-left bg-amber-50">Toko / Supplier</th>
                  <th className="px-2 py-2 text-left bg-amber-50">Bukti (Foto/PDF)</th>
                  <th className="px-2 py-2 text-left">Catatan</th>
                </tr>
              </thead>
              <tbody>
                {(pc.items || []).map((it, i) => {
                  // 2026-06-10: fallback key per-index utk item legacy tanpa id.
                  const rowKey = it.id || `idx-${i}`;
                  const cur = items[rowKey] || {};
                  const actAmt = Number(String(cur.actual_amount ?? "").replace(/[^\d.-]/g, "")) || 0;
                  const delta = actAmt - (it.subtotal || 0);
                  // Bukti transaksi: state lokal (baru upload/hapus) menimpa data server.
                  const proof = rowKey in proofs ? proofs[rowKey] : (it.proof_file || null);
                  return (
                    <tr key={rowKey} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pc-actual-row-${rowKey}`}>
                      <td className="px-2 py-2 align-top" title={it.name}>
                        <div
                          className="font-medium text-[#0B1D3A]"
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "normal",
                            overflowWrap: "break-word",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                          data-testid={`pc-actual-name-${rowKey}`}
                        >
                          {it.name}
                        </div>
                        {it.note && (
                          <div
                            className="text-[10px] text-slate-500 italic mt-0.5"
                            style={{ whiteSpace: "normal", wordBreak: "normal", overflowWrap: "break-word", lineHeight: 1.35 }}
                            title={it.note}
                          >
                            {it.note}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{it.qty}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{formatIDR(it.unit_price)}</td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold bg-slate-50">{formatIDR(it.subtotal)}</td>
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[140px]">
                        <DateField testid={`pc-actual-date-${rowKey}`} value={cur.actual_transaction_date || ""} onChange={(v) => onItemFieldChange(rowKey, "actual_transaction_date", v)} />
                      </td>
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[140px]">
                        <DateField testid={`pc-actual-voucher-date-${rowKey}`} value={cur.voucher_date || ""} onChange={(v) => onItemFieldChange(rowKey, "voucher_date", v)} />
                      </td>
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[140px]">
                        <RupiahInput testid={`pc-actual-amount-${rowKey}`} value={cur.actual_amount ?? ""} onChange={(v) => onItemFieldChange(rowKey, "actual_amount", v)} placeholder={String(it.subtotal || 0)} className="h-9" />
                        {delta !== 0 && (
                          <div className={`text-[9px] tabular-nums mt-0.5 ${delta > 0 ? "text-amber-700" : "text-emerald-700"}`} title={delta > 0 ? "Melebihi pengajuan — diperbolehkan" : "Hemat"}>
                            {delta > 0 ? "▲ Lebih " : "▼ Hemat "}{formatIDR(Math.abs(delta))}
                          </div>
                        )}
                      </td>
                      {/* 149-cw-r190: bukti transaksi — nomor voucher & toko/supplier. */}
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[120px]">
                        <Input data-testid={`pc-actual-voucher-${rowKey}`} value={cur.voucher_no || ""} onChange={(e) => onItemFieldChange(rowKey, "voucher_no", e.target.value)} className="rounded-none h-8 text-xs" placeholder="mis. VCH-001" />
                      </td>
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[140px]">
                        <Input data-testid={`pc-actual-supplier-${rowKey}`} value={cur.supplier || ""} onChange={(e) => onItemFieldChange(rowKey, "supplier", e.target.value)} className="rounded-none h-8 text-xs" placeholder="mis. Toko ABC" />
                      </td>
                      {/* 2026-10-16 (user request): upload bukti transaksi (foto/PDF) —
                          dicek Verifikasi 1 terhadap nominal yang diinput. */}
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[170px]" data-testid={`pc-actual-proof-cell-${rowKey}`}>
                        {!it.id ? (
                          <span className="text-[9px] text-slate-400 italic">Simpan realisasi dulu untuk upload bukti</span>
                        ) : (
                          <div className="space-y-1">
                            {proof ? (
                              <div className="flex items-center gap-1.5">
                                <a href={pcProofUrl(pc.id, it.id)} target="_blank" rel="noreferrer"
                                  data-testid={`pc-actual-proof-view-${rowKey}`}
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 hover:text-sky-900 underline truncate max-w-[110px]"
                                  title={`${proof.name} · diupload ${proof.uploaded_by || ""}`}>
                                  <Paperclip size={10} /> {proof.name || "Bukti"}
                                </a>
                                {it.item_verified !== true && (
                                  <button type="button" onClick={() => deleteProof(rowKey, it.id)} disabled={proofBusy === rowKey}
                                    data-testid={`pc-actual-proof-del-${rowKey}`} title="Hapus bukti"
                                    className="p-0.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-40">
                                    <Trash2 size={11} />
                                  </button>
                                )}
                              </div>
                            ) : null}
                            {it.item_verified !== true && (
                              <label className={`inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase tracking-wider font-semibold cursor-pointer transition-colors ${proof ? "border-slate-300 text-slate-600 hover:bg-slate-100" : "border-amber-500 text-amber-700 hover:bg-amber-100"} ${proofBusy === rowKey ? "opacity-50 pointer-events-none" : ""}`}
                                data-testid={`pc-actual-proof-upload-${rowKey}`}>
                                {proofBusy === rowKey ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                                {proof ? "Ganti" : "Upload"}
                                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                                  data-testid={`pc-actual-proof-input-${rowKey}`}
                                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadProof(rowKey, it.id, f); }} />
                              </label>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <Input data-testid={`pc-actual-note-${rowKey}`} value={cur.note || ""} onChange={(e) => onItemFieldChange(rowKey, "note", e.target.value)} className="rounded-none h-8 text-xs" placeholder="opsional…" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                <tr>
                  <td colSpan={3} className="px-2 py-2 text-right">TOTAL</td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-700">{formatIDR(totalPengajuan)}</td>
                  <td className="px-2 py-2" colSpan={2}></td>
                  <td className="px-2 py-2 text-right tabular-nums text-amber-700" data-testid="pc-actual-total-footer">{formatIDR(totalAktual)}</td>
                  <td className="px-2 py-2" colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Catatan Keseluruhan (opsional)</label>
            <Textarea data-testid="input-actual-doc-note" value={docNote} onChange={(e) => onDocNoteChange(e.target.value)} className="rounded-none text-sm" rows={2} placeholder="mis. sisa Rp X dikembalikan ke kas / dipakai untuk minggu depan" />
          </div>

          <div className="border-l-2 border-amber-400 bg-amber-50 p-2.5 text-[10px] text-amber-800">
            Lampirkan <b>bukti transaksi</b> (foto nota/struk atau PDF) di kolom <b>Bukti</b> — pemeriksa Verifikasi 1 akan mengecek kesesuaian nominal yang Anda input dengan bukti tersebut.
            Setelah tersimpan, FAT/BOD melakukan <b>Verifikasi 1</b> (pengeluaran) per baris di Petty Cash Detail, lalu <b>Verifikasi 2</b> (penetapan COA). Nominal aktual yang lolos Verifikasi 1 mengurangi saldo petty cash Anda. Item yang dicentang <b>Tdk Jadi</b> tidak perlu diverifikasi — bila semua item dibatalkan, pengajuan selesai otomatis.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-none">Batal</Button>
          <Button data-testid="btn-submit-actual" onClick={onSubmit} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white">Simpan Realisasi Per Item</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
